import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoveRight, PhoneCall, MapPin, Mail, Phone, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 rounded-[2px] border-[#3A4536]/20 bg-[#EAE5D9] text-[#1A2118] placeholder:text-[#3A4536]/70 focus-visible:ring-1 focus-visible:ring-[#B85D43]";

export default function ContactCallback() {
  const { t } = useLang();
  const productOptions = [
    { value: "Botanical Ingredients", label: t("divisions.botanical_name") },
    { value: "Agri Commodities", label: t("divisions.agri_name") },
    { value: "Value-Added Products", label: t("divisions.value-added_name") },
    { value: "General / Multiple", label: t("contact.opt_general") },
  ];
  const [contact, setContact] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    product_interest: "",
    message: "",
    hp: "",
  });
  const [callback, setCallback] = useState({
    name: "",
    phone: "",
    country: "",
    product_interest: "",
    preferred_time: "",
    hp: "",
  });
  const [loadingC, setLoadingC] = useState(false);
  const [loadingCb, setLoadingCb] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.country || !contact.product_interest || !contact.message) {
      toast.error(t("contact.required"));
      return;
    }
    setLoadingC(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success(t("contact.ok_contact"));
      setContact({ name: "", email: "", company: "", country: "", product_interest: "", message: "", hp: "" });
    } catch (err) {
      const msg = err?.response?.status === 429 ? t("contact.ratelimit") : t("contact.error");
      toast.error(msg);
    } finally {
      setLoadingC(false);
    }
  };

  const submitCallback = async (e) => {
    e.preventDefault();
    if (!callback.name || !callback.phone || !callback.country || !callback.product_interest) {
      toast.error(t("contact.required"));
      return;
    }
    setLoadingCb(true);
    try {
      await axios.post(`${API}/callback`, callback);
      toast.success(t("contact.ok_callback"));
      setCallback({ name: "", phone: "", country: "", product_interest: "", preferred_time: "", hp: "" });
    } catch (err) {
      const msg = err?.response?.status === 429 ? t("contact.ratelimit") : t("contact.error");
      toast.error(msg);
    } finally {
      setLoadingCb(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="border-t border-[#3A4536]/15 bg-[#1A2118] text-[#F4F1EB]"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-10 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 max-w-3xl"
        >
          <div className="mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[#D4A373]" />
            <span className="overline text-[#D4A373]">{t("contact.overline")}</span>
          </div>
          <h2 className="font-serif-display text-5xl font-light leading-[0.95] md:text-7xl">
            {t("contact.h1")}
            <br />
            {t("contact.h2")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Contact inquiry form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <h3 className="mb-8 font-serif-display text-3xl font-light text-[#F4F1EB]">
              {t("contact.send")}
            </h3>
            <form data-testid="contact-form" onSubmit={submitContact} className="space-y-5">
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={contact.hp}
                onChange={(e) => setContact({ ...contact, hp: e.target.value })}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field label={`${t("contact.name")} *`}>
                  <Input data-testid="input-name" className={inputCls} placeholder={t("contact.ph_name")}
                    value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                </Field>
                <Field label={`${t("contact.email")} *`}>
                  <Input data-testid="input-email" type="email" className={inputCls} placeholder={t("contact.ph_email")}
                    value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                </Field>
                <Field label={t("contact.company")}>
                  <Input data-testid="input-company" className={inputCls} placeholder={t("contact.ph_company")}
                    value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
                </Field>
                <Field label={`${t("contact.country")} *`}>
                  <Input data-testid="input-country" className={inputCls} placeholder={t("contact.ph_country")}
                    value={contact.country} onChange={(e) => setContact({ ...contact, country: e.target.value })} />
                </Field>
              </div>
              <Field label={`${t("contact.product")} *`}>
                <Select value={contact.product_interest}
                  onValueChange={(v) => setContact({ ...contact, product_interest: v })}>
                  <SelectTrigger data-testid="select-product-interest" className={inputCls}>
                    <SelectValue placeholder={t("contact.ph_product")} />
                  </SelectTrigger>
                  <SelectContent>
                    {productOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`option-${o.value}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`${t("contact.message")} *`}>
                <Textarea data-testid="input-message" rows={4}
                  className="rounded-[2px] border-[#3A4536]/20 bg-[#EAE5D9] text-[#1A2118] placeholder:text-[#3A4536]/70 focus-visible:ring-1 focus-visible:ring-[#B85D43]"
                  placeholder={t("contact.ph_message")}
                  value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
              </Field>
              <button data-testid="btn-submit-contact" type="submit" disabled={loadingC}
                className="group flex items-center gap-3 rounded-full bg-[#B85D43] px-8 py-4 text-sm text-[#F4F1EB] transition-colors hover:bg-[#D4A373] hover:text-[#1A2118] disabled:opacity-60">
                {loadingC ? <Loader2 className="h-4 w-4 animate-spin" /> : t("contact.submit_contact")}
                {!loadingC && <MoveRight strokeWidth={1.5} className="h-4 w-4 transition-transform group-hover:translate-x-1.5" />}
              </button>
            </form>
          </motion.div>

          {/* Callback + contact info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="rounded-[2px] border border-[#F4F1EB]/15 bg-[#F4F1EB]/[0.04] p-8">
              <div className="mb-6 flex items-center gap-3">
                <PhoneCall strokeWidth={1.5} className="h-5 w-5 text-[#D4A373]" />
                <h3 className="font-serif-display text-3xl font-light text-[#F4F1EB]">
                  {t("contact.callback")}
                </h3>
              </div>
              <form data-testid="callback-form" onSubmit={submitCallback} className="space-y-5">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={callback.hp}
                  onChange={(e) => setCallback({ ...callback, hp: e.target.value })}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <Field label={`${t("contact.name")} *`}>
                  <Input data-testid="input-cb-name" className={inputCls} placeholder={t("contact.ph_name")}
                    value={callback.name} onChange={(e) => setCallback({ ...callback, name: e.target.value })} />
                </Field>
                <Field label={`${t("contact.phone")} *`}>
                  <Input data-testid="input-cb-phone" className={inputCls} placeholder={t("contact.ph_phone")}
                    value={callback.phone} onChange={(e) => setCallback({ ...callback, phone: e.target.value })} />
                </Field>
                <Field label={`${t("contact.country")} *`}>
                  <Input data-testid="input-cb-country" className={inputCls} placeholder={t("contact.ph_country")}
                    value={callback.country} onChange={(e) => setCallback({ ...callback, country: e.target.value })} />
                </Field>
                <Field label={`${t("contact.product")} *`}>
                  <Select value={callback.product_interest}
                    onValueChange={(v) => setCallback({ ...callback, product_interest: v })}>
                    <SelectTrigger data-testid="select-cb-product" className={inputCls}>
                      <SelectValue placeholder={t("contact.ph_product")} />
                    </SelectTrigger>
                    <SelectContent>
                      {productOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`cb-option-${o.value}`}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("contact.time")}>
                  <Input data-testid="input-cb-time" className={inputCls} placeholder={t("contact.ph_time")}
                    value={callback.preferred_time} onChange={(e) => setCallback({ ...callback, preferred_time: e.target.value })} />
                </Field>
                <button data-testid="btn-submit-callback" type="submit" disabled={loadingCb}
                  className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#F4F1EB] px-8 py-4 text-sm text-[#1A2118] transition-colors hover:bg-[#D4A373] disabled:opacity-60">
                  {loadingCb ? <Loader2 className="h-4 w-4 animate-spin" /> : t("contact.submit_callback")}
                  {!loadingCb && <PhoneCall strokeWidth={1.5} className="h-4 w-4" />}
                </button>
              </form>
            </div>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4">
                <MapPin strokeWidth={1.5} className="mt-0.5 h-5 w-5 text-[#D4A373]" />
                <p className="text-sm leading-relaxed text-[#F4F1EB]/70">
                  HERBLABS Private Limited<br />
                  274/15, Luxmi Nagar, Gohana,<br />
                  Haryana, 131301, India
                </p>
              </div>
              <div className="flex items-start gap-4">
                <Mail strokeWidth={1.5} className="mt-0.5 h-5 w-5 text-[#D4A373]" />
                <div className="text-sm leading-relaxed text-[#F4F1EB]/70">
                  <a href="mailto:lakshay@herblabs.in" className="link-underline block">lakshay@herblabs.in</a>
                  <a href="mailto:lakshayd201@gmail.com" className="link-underline block">lakshayd201@gmail.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone strokeWidth={1.5} className="mt-0.5 h-5 w-5 text-[#D4A373]" />
                <div className="text-sm leading-relaxed text-[#F4F1EB]/70">
                  <a href="tel:+918569872472" className="link-underline block">+91 85698 72472</a>
                  <a href="tel:+919991997902" className="link-underline block">+91 99919 97902</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <MapPin strokeWidth={1.5} className="h-5 w-5 text-[#D4A373]" />
                <span className="text-sm text-[#F4F1EB]/70">{t("contact.worldwide")}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="overline text-[#F4F1EB]/60">{label}</Label>
      {children}
    </div>
  );
}
